import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCartWishlist } from '@/context/CartWishlistContext';
import StarRating from './StarRating';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSection = ({ productId, product }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myReview, setMyReview] = useState(null);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const { user } = useCartWishlist();

  useEffect(() => {
    fetchReviews();
    if (user) {
      checkMyReview();
    }
  }, [productId, sortBy, user]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/reviews?sort=${sortBy}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkMyReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/my-review/${productId}`, { 
        withCredentials: true 
      });
      setHasReviewed(response.data.has_reviewed);
      setMyReview(response.data.review);
    } catch (error) {
      console.error('Failed to check review status:', error);
    }
  };

  const handleSubmitReview = async ({ rating, comment }) => {
    try {
      if (editingReview) {
        // Update existing review
        await axios.put(
          `${API}/reviews/${editingReview.id}?rating=${rating}&comment=${encodeURIComponent(comment || '')}`,
          {},
          { withCredentials: true }
        );
        toast.success('Review updated successfully!');
      } else {
        // Add new review
        await axios.post(
          `${API}/products/${productId}/reviews?rating=${rating}&comment=${encodeURIComponent(comment || '')}`,
          {},
          { withCredentials: true }
        );
        toast.success('Review added successfully!');
      }
      
      setShowReviewForm(false);
      setEditingReview(null);
      fetchReviews();
      checkMyReview();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit review');
      }
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted successfully');
      fetchReviews();
      checkMyReview();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth-callback`;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  const calculateRatingBreakdown = () => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      breakdown[review.rating]++;
    });
    return breakdown;
  };

  const ratingBreakdown = calculateRatingBreakdown();

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-red-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="review-section">
      {/* Reviews Header */}
      <div className="border-t border-white/10 pt-8">
        <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
        
        {/* Rating Summary */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Average Rating */}
          <div className="glass-effect p-6 rounded-xl">
            <div className="flex items-center space-x-4 mb-4">
              <div className="text-5xl font-bold text-red-500">
                {product?.rating?.toFixed(1) || '0.0'}
              </div>
              <div>
                <StarRating rating={Math.round(product?.rating || 0)} readonly={true} size={24} />
                <p className="text-gray-400 mt-1">
                  Based on {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                </p>
              </div>
            </div>
          </div>

          {/* Rating Breakdown */}
          <div className="glass-effect p-6 rounded-xl">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(star => {
                const count = ratingBreakdown[star];
                const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                return (
                  <div key={star} className="flex items-center space-x-3">
                    <span className="text-sm w-8">{star}★</span>
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-400 w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Add Review Button / Form */}
        {user ? (
          !hasReviewed && !showReviewForm ? (
            <button
              onClick={() => setShowReviewForm(true)}
              className="btn-primary mb-8"
              data-testid="write-review-btn"
            >
              Write a Review
            </button>
          ) : showReviewForm ? (
            <div className="mb-8">
              <ReviewForm
                productId={productId}
                existingReview={editingReview}
                onSubmit={handleSubmitReview}
                onCancel={() => {
                  setShowReviewForm(false);
                  setEditingReview(null);
                }}
              />
            </div>
          ) : null
        ) : (
          <div className="glass-effect p-6 rounded-xl mb-8 text-center">
            <p className="text-gray-400 mb-4">Please login to write a review</p>
            <button onClick={handleLogin} className="btn-primary">
              Login
            </button>
          </div>
        )}

        {/* Sort Options */}
        {reviews.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">All Reviews ({reviews.length})</h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
              data-testid="sort-reviews"
            >
              <option value="recent">Most Recent</option>
              <option value="highest">Highest Rated</option>
              <option value="lowest">Lowest Rated</option>
            </select>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          {reviews.length > 0 ? (
            reviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                isOwnReview={user?.id === review.user_id}
                onEdit={handleEditReview}
                onDelete={handleDeleteReview}
              />
            ))
          ) : (
            <div className="text-center py-12 glass-effect rounded-xl">
              <Star className="mx-auto mb-4 text-gray-600" size={64} />
              <h3 className="text-xl font-semibold mb-2">No reviews yet</h3>
              <p className="text-gray-400">Be the first to review this product!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewSection;